import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Music2, User, Users, ArrowRight } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { Logo } from '../components/Logo';
import MusicServicesDialog from '../components/MusicServicesDialog';

interface CompareStats {
  matchScore: number;
  commonArtists: string[];
  commonGenres: string[];
  topMatches: Array<{
    title: string;
    artist: string;
    album: string;
    albumCover: string;
  }>;
}

function Compare() {
  const { user } = useUser();
  const [showMusicServices, setShowMusicServices] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [compareStats, setCompareStats] = useState<CompareStats | null>(null);
  const [loading, setLoading] = useState(false);

  const handleCompare = async (userId: string) => {
    setLoading(true);
    try {
      // This would be replaced with actual API calls to music services
      // For now, showing mock data
      setCompareStats({
        matchScore: 78,
        commonArtists: ['The Beatles', 'Pink Floyd', 'Led Zeppelin'],
        commonGenres: ['Classic Rock', 'Progressive Rock', 'Psychedelic'],
        topMatches: [
          {
            title: 'Stairway to Heaven',
            artist: 'Led Zeppelin',
            album: 'Led Zeppelin IV',
            albumCover: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=100&h=100&fit=crop'
          },
          {
            title: 'Wish You Were Here',
            artist: 'Pink Floyd',
            album: 'Wish You Were Here',
            albumCover: 'https://images.unsplash.com/photo-1520962880247-cfaf541c8724?w=100&h=100&fit=crop'
          }
        ]
      });
    } catch (error) {
      console.error('Error comparing music tastes:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="mobile-header">
        <div className="mobile-header-content">
          <div className="flex items-center gap-2">
            <Logo className="h-8 w-8 text-indigo-600" />
            <span className="text-lg font-bold text-indigo-600">Compare</span>
          </div>
        </div>
      </header>

      <div className="mobile-main pt-2">
        <div className="max-w-2xl mx-auto space-y-4">
          {!user?.spotifyConnected && !user?.appleMusicConnected ? (
            <div className="bg-white p-8 rounded-lg shadow-sm border-2 border-gray-200 text-center">
              <Music2 className="h-12 w-12 text-indigo-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Connect Your Music</h3>
              <p className="text-gray-600 mb-6">
                Connect your Spotify or Apple Music account to compare music taste with friends
              </p>
              <button 
                onClick={() => setShowMusicServices(true)}
                className="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Connect Music Services
              </button>
            </div>
          ) : (
            <>
              <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">Your Music Profile</h2>
                  <button
                    onClick={() => setShowMusicServices(true)}
                    className="text-indigo-600 hover:text-indigo-700 transition-colors"
                  >
                    Manage Services
                  </button>
                </div>

                <div className="space-y-4">
                  {user.spotifyConnected && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Music2 className="h-5 w-5 text-green-600" />
                          <span className="font-medium text-green-900">Spotify Connected</span>
                        </div>
                        <span className="text-green-600">✓</span>
                      </div>
                    </div>
                  )}

                  {user.appleMusicConnected && (
                    <div className="p-4 bg-pink-50 rounded-lg border border-pink-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Music2 className="h-5 w-5 text-pink-600" />
                          <span className="font-medium text-pink-900">Apple Music Connected</span>
                        </div>
                        <span className="text-pink-600">✓</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {compareStats ? (
                <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-200">
                  <h3 className="text-xl font-semibold mb-6">Music Match Results</h3>
                  
                  <div className="space-y-6">
                    <div className="text-center">
                      <div className="inline-block w-24 h-24 rounded-full bg-indigo-100 flex items-center justify-center mb-2">
                        <span className="text-3xl font-bold text-indigo-600">{compareStats.matchScore}%</span>
                      </div>
                      <p className="text-gray-600">Music Match Score</p>
                    </div>

                    <div>
                      <h4 className="font-medium mb-2">Common Artists</h4>
                      <div className="flex flex-wrap gap-2">
                        {compareStats.commonArtists.map(artist => (
                          <span key={artist} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                            {artist}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium mb-2">Common Genres</h4>
                      <div className="flex flex-wrap gap-2">
                        {compareStats.commonGenres.map(genre => (
                          <span key={genre} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                            {genre}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium mb-2">Top Matches</h4>
                      <div className="space-y-3">
                        {compareStats.topMatches.map(song => (
                          <div key={song.title} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <img
                              src={song.albumCover}
                              alt={song.album}
                              className="w-12 h-12 rounded-md object-cover"
                            />
                            <div>
                              <p className="font-medium">{song.title}</p>
                              <p className="text-sm text-gray-600">{song.artist}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-200">
                  <div className="text-center">
                    <Users className="h-12 w-12 text-indigo-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Compare with Friends</h3>
                    <p className="text-gray-600 mb-6">
                      Visit a friend's profile and click "Compare Music" to see your music compatibility
                    </p>
                    <Link
                      to="/explore"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      Find Friends
                      <ArrowRight className="h-5 w-5" />
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}

          <MusicServicesDialog
            isOpen={showMusicServices}
            onClose={() => setShowMusicServices(false)}
          />
        </div>
      </div>
    </div>
  );
}

export default Compare;